import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "./AuthContext";

/* ============================================================
   /admin/login — Two-step login (password → email 2FA)
============================================================ */

const CODE_LENGTH = 6;

export default function LoginPage() {
  const auth = useAdminAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ticket, setTicket] = useState(null);
  const [emailMasked, setEmailMasked] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const codeInputRef = useRef(null);

  /* --- cooldown tick for the "resend code" button --- */
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /* focus code input when step 2 opens */
  useEffect(() => {
    if (step === 2 && codeInputRef.current) codeInputRef.current.focus();
  }, [step]);

  if (auth.user) {
    return <Navigate to={loc.state?.from?.pathname || "/admin"} replace />;
  }

  const onSubmitStep1 = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await auth.login(email, password);
      setTicket(r.ticket);
      setEmailMasked(r.email_masked || email);
      setCooldown(r.resend_cooldown || 45);
      setCode("");
      setStep(2);
    } catch (e2) {
      setErr(e2.message || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  const onSubmitStep2 = async (e) => {
    e.preventDefault();
    setErr("");
    if (code.length !== CODE_LENGTH) {
      setErr(`Enter the ${CODE_LENGTH}-digit code from your email.`);
      return;
    }
    setBusy(true);
    try {
      await auth.verify2fa(ticket, code);
      nav(loc.state?.from?.pathname || "/admin", { replace: true });
    } catch (e2) {
      setErr(e2.message || "Verification failed.");
      // If the ticket was burned, force the user back to step 1
      if (e2.status === 410) {
        setTimeout(() => {
          setStep(1);
          setTicket(null);
          setCode("");
          setPassword("");
        }, 1500);
      }
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (cooldown > 0 || busy || !ticket) return;
    setErr("");
    setBusy(true);
    try {
      const r = await auth.resend2fa(ticket);
      setCooldown(r.resend_cooldown || 45);
      setCode("");
      if (codeInputRef.current) codeInputRef.current.focus();
    } catch (e2) {
      setErr(e2.message || "Couldn't send a new code.");
    } finally {
      setBusy(false);
    }
  };

  const onCodeChange = (raw) => {
    const cleaned = (raw || "").replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(cleaned);
    setErr("");
  };

  return (
    <div className="min-h-screen bg-[#0B0F14] text-neutral-100 flex items-center justify-center px-6">
      {step === 1 ? (
        <form
          onSubmit={onSubmitStep1}
          className="w-full max-w-sm"
          data-testid="admin-login-form"
          autoComplete="off"
        >
          <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
            Sunday · Admin
          </div>
          <h1 className="mt-3 font-display text-3xl font-black tracking-[-0.025em] leading-[1.05]">
            Sign in.
          </h1>

          <label className="block mt-9 text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
            Email
          </label>
          <input
            type="email"
            name="admin-email-no-fill"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@company.com"
            data-testid="admin-login-email"
            className="mt-2 w-full bg-[#0F141A] border border-white/[0.10] rounded-md px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#F4D64E]/60 transition-colors"
          />

          <label className="block mt-5 text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
            Password
          </label>
          <input
            type="password"
            name="admin-password-no-fill"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="admin-login-password"
            className="mt-2 w-full bg-[#0F141A] border border-white/[0.10] rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 transition-colors"
          />

          {err ? (
            <div className="mt-5 text-[0.78rem] text-red-400" data-testid="admin-login-error">
              {err}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-7 w-full inline-flex items-center justify-center text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3.5 rounded-md transition-colors"
          >
            {busy ? "Checking…" : "Continue"}
          </button>
        </form>
      ) : (
        <form
          onSubmit={onSubmitStep2}
          className="w-full max-w-sm"
          data-testid="admin-2fa-form"
          autoComplete="off"
        >
          <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
            Sunday · Admin · Step 2 of 2
          </div>
          <h1 className="mt-3 font-display text-3xl font-black tracking-[-0.025em] leading-[1.05]">
            Check your email.
          </h1>
          <p className="mt-3 text-sm text-neutral-400">
            We sent a 6-digit code to{" "}
            <span className="text-white font-semibold" data-testid="admin-2fa-email-masked">
              {emailMasked}
            </span>
            . It expires in 10 minutes.
          </p>

          <label className="block mt-9 text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
            Verification code
          </label>
          <input
            ref={codeInputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            placeholder="••••••"
            data-testid="admin-2fa-code"
            className="mt-2 w-full bg-[#0F141A] border border-white/[0.10] rounded-md px-4 py-4 text-center text-[1.65rem] tracking-[0.5em] text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#F4D64E]/60 transition-colors font-mono"
          />

          {err ? (
            <div className="mt-5 text-[0.78rem] text-red-400" data-testid="admin-2fa-error">
              {err}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={busy || code.length !== CODE_LENGTH}
            data-testid="admin-2fa-submit"
            className="mt-7 w-full inline-flex items-center justify-center text-[0.78rem] font-bold tracking-[0.22em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3.5 rounded-md transition-colors"
          >
            {busy ? "Verifying…" : "Verify & sign in"}
          </button>

          <div className="mt-5 flex items-center justify-between text-[0.75rem] text-neutral-500">
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setTicket(null);
                setCode("");
                setErr("");
                setPassword("");
              }}
              className="hover:text-[#F4D64E] transition-colors"
              data-testid="admin-2fa-back"
            >
              ← Use a different email
            </button>
            <button
              type="button"
              onClick={onResend}
              disabled={cooldown > 0 || busy}
              data-testid="admin-2fa-resend"
              className="hover:text-[#F4D64E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
